<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center flex-wrap">
                    <b-form-select
                        id="grupo-select"
                        v-model="filtroGrupo"
                        @change="onGrupoChange"
                        class="filter-select"
                    >
                        <b-form-select-option :value="null">
                            {{ labels.selectGroup }}
                        </b-form-select-option>
                        <option v-for="grupo in listGrupos" :key="grupo.id" :value="grupo.id">
                            {{ grupo.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-form-select
                        id="compania-select"
                        v-model="filtroCompania"
                        @change="onFiltroChange"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option :value="null">
                            {{ labels.selectCompany }}
                        </b-form-select-option>
                        <option v-for="compania in filteredCompany" :key="compania.id" :value="compania.id">
                            {{ compania.nombre }}
                        </option>
                    </b-form-select>
                    
                    <b-button
                        class="consult-btn ml-2"
                        @click="consultarDatos"
                        :disabled="!filtroCompania || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <div class="admin-table-block mt-4">
            <!-- ESTADO INICIAL - Sin datos y sin consulta -->
            <div v-if="!fgl_consulta && !isLoading && kardexList.length === 0">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- LOADING -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- TABLA CON DATOS -->
            <div v-if="!isLoading && fgl_consulta && kardexList.length > 0">
                <CorporateDataTable
                    :items="itemsFiltrados"
                    :fields="camposTabla"
                    :labels="tableLabels"
                    :default-per-page="perPage"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="490"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :toolbar-disabled="!showToolbarExtras"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                >
                    <template #toolbar-actions>
                        <div class="kardex-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="abrirModalCrear"
                                :disabled="!showToolbarExtras || isVigenciaBloqueada || !filtroCompania"
                            >
                                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                                {{ labels.create }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="table-toolbar-export-btn ml-1"
                                @click="exportarExcel"
                                :disabled="kardexList.length === 0"
                            >
                                <font-awesome-icon :icon="['fas', 'file-excel']" class="mr-1" />
                                {{ labels.exportExcel }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(cuenta)="data">
                        <div class="text-left">{{ data.item.cuenta }}</div>
                    </template>

                    <template #cell(fecha_operacion)="data">
                        <div class="text-center">{{ data.item.fecha_operacion }}</div>
                    </template>

                    <template #cell(concepto)="data">
                        <div class="text-left">{{ data.item.concepto }}</div>
                    </template>

                    <template #cell(local)="data">
                        <div class="text-right">{{ formatNumber(data.item.local) }}</div>
                    </template>

                    <template #cell(trm)="data">
                        <div class="text-right">{{ formatNumber(data.item.trm) }}</div>
                    </template>

                    <template #cell(funcional1)="data">
                        <div class="text-right">{{ formatNumber(data.item.funcional1) }}</div>
                    </template>

                    <template #cell(accionEdit)="data">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0;"
                            class="md-fab md-primary table-action-btn"
                            @click="editarKardex(data.item.id)"
                            :disabled="isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs"/></i>
                        </b-button>
                    </template>

                    <!-- <template #cell(accionDelete)="data">
                        <b-button
                            class="reset-btn-danger table-action-btn"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center;"
                            @click.stop="deleteKardex(data.item.id)"
                            :disabled="isVigenciaBloqueada"
                        >
                            <i><font-awesome-icon :icon="['fas', 'trash']" size="xs"/></i>
                        </b-button>
                    </template> -->

                </CorporateDataTable>
            </div>

            <!-- TABLA VACÍA - Consulta realizada sin resultados -->
            <div v-if="!isLoading && fgl_consulta && kardexList.length === 0" class="text-center py-5">
                <font-awesome-icon
                    :icon="['fas', 'table']"
                    size="3x"
                    class="text-muted mb-3"
                    style="opacity: 0.3;"
                />
                <p class="h4 text-muted">
                    {{ labels.noData }}
                </p>
                <p class="text-muted">
                    {{ labels.createFirstRecord }}
                </p>
            </div>
        </div>

        <!-- Modal para CREAR/EDITAR kardex -->
        <b-modal
            :model-value="showModalKardex"
            :no-close-on-backdrop="true"
            hide-footer
            centered
            size="lg"
            :title="modalTitle"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
            class="reset-password-dialog edit-kardex-dialog"
            @hide="cerrarModal"
        >
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ modalInfoTitle }}</div>
                    <div class="password-policy-subtitle">{{ modalInfoText }}</div>
                </div>

                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.date }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input
                            v-model="formData.fecha_operacion"
                            type="date"
                            class="form-input"
                            required
                            @change="onFechaChange"
                            :disabled="modoEdicion || isVigenciaBloqueada"
                        />
                        <small v-if="isVigenciaBloqueada" class="text-danger">
                            Periodo cerrado - No se puede editar
                        </small>
                    </b-col>
                </b-row>

                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.account }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-select
                            v-model="formData.cuenta"
                            class="form-input"
                            :disabled="!formData.fecha_operacion || isVigenciaBloqueada"
                            @change="onChange"
                        >
                            <b-form-select-option :value="null">
                                -- {{ labels.selectAccount }} --
                            </b-form-select-option>
                            <option v-for="cuenta in cuentasDisponibles" :key="cuenta" :value="cuenta">
                                {{ cuenta }}
                            </option>
                        </b-form-select>
                        <small v-if="vigenciaEncontrada" class="text-muted">
                            {{ labels.validity }}: {{ vigenciaEncontrada }}
                        </small>
                    </b-col>
                </b-row>

                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.concept }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input
                            v-model="formData.concepto"
                            type="text"
                            class="form-input"
                            :placeholder="labels.conceptPlaceholder"
                            list="conceptos-list"
                            required
                            :disabled="isVigenciaBloqueada"
                            @input="onChange"
                        />
                        <datalist id="conceptos-list">
                            <option v-for="concepto in conceptosExistentes" :key="concepto" :value="concepto"></option>
                        </datalist>
                    </b-col>
                </b-row>

                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.balance }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input
                            v-model="formData.local"
                            type="number"
                            step="0.01"
                            class="form-input"
                            placeholder="0.00"
                            required
                            @input="calcularFuncional"
                            :disabled="isVigenciaBloqueada"
                        />
                    </b-col>
                </b-row>

                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.trm }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input
                            v-model="formData.trm"
                            type="number"
                            step="0.0001"
                            class="form-input"
                            placeholder="0.0000"
                            required
                            @input="calcularFuncional"
                            :disabled="isVigenciaBloqueada"
                        />
                    </b-col>
                </b-row>

                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.functionalBalance }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input
                            v-model="formData.funcional1"
                            type="number"
                            step="0.01"
                            class="form-input"
                            placeholder="0.00"
                            @input="calcularTRM"
                            :disabled="isVigenciaBloqueada"
                        />
                        <small class="text-muted">
                            {{ formData.local && formData.trm ? `${formatCurrency(formData.local * formData.trm)}` : labels.waitingData }}
                        </small>
                    </b-col>
                </b-row>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="cerrarModal">
                        {{ labels.cancel }}
                    </b-button>
                    <b-button 
                        class="reset-btn-primary ml-2" 
                        type="submit"
                        @click="guardarKardex"
                        :disabled="!hasUnsavedChanges || guardando || isVigenciaBloqueada"
                    >
                        <b-spinner v-if="guardando" small class="mr-1"></b-spinner>
                        {{ guardando ? labels.saving : labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- Modal de Confirmación para Eliminar -->
        <b-modal
            :model-value="showModDel"
            :no-close-on-backdrop="true"
            hide-footer
            centered
            size="md"
            :title="confirmLabels.title"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
            class="reset-password-dialog confirm-dialog"
            @hide="closeModal"
        >
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ confirmLabels.infoTitle }}</div>
                    <div class="password-policy-subtitle">{{ confirmLabels.infoText }}</div>
                </div>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="closeModal">
                        {{ confirmLabels.cancel }}
                    </b-button>
                    <b-button class="reset-btn-danger ml-2" @click="confirmDel">
                        {{ confirmLabels.confirm }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import TasasKardexService from '@/services/TasasKardex'
import Company from "@/services/Company"
import Grupo from "@/services/Grupo"
import CorporateDataTable from '../utils/CorporateDataTable.vue'
import * as XLSX from "exceljs"

export default {
    name: "TasasKardex",
    components: {
        CorporateDataTable
    },
    data() {
        return {
            // Datos
            kardexList: [],
            companiasList: [],
            listGrupos: [],
            conceptosExistentes: [],
            cuentasDisponibles: [],
            vigenciaEncontrada: null,
            
            // Filtros
            filtroGrupo: null,
            filtroCompania: null,
            filteredCompany: [],
            
            // Estado
            isLoading: false,
            guardando: false,
            fgl_consulta: false,
            modoEdicion: false,
            kardexEditId: null,
            showModalKardex: false,
            showModDel: false,
            rowDelete: null,
            
            // Búsqueda y paginación
            searchTerm: '',
            currentPage: 1,
            perPage: 10,
            sortBy: 'fecha_operacion',
            sortDesc: true,
            
            // Formulario modal
            formData: {
                fecha_operacion: null,
                cuenta: null,
                concepto: '',
                local: null,
                trm: null,
                funcional1: null,
                funcional2: null
            },
            originalFormData: null
        }
    },
    computed: {
        labels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Kardex de Patrimonio",
                    selectGroup: "Seleccione Grupo",
                    selectCompany: "Seleccione Compañía",
                    selectAccount: "Seleccione una cuenta",
                    consultar: "Consultar",
                    create: "Crear",
                    exportExcel: "Exportar Excel",
                    createKardex: "Nuevo Kardex",
                    editKardex: "Editar Kardex",
                    edit: "Editar",
                    delete: "Eliminar",
                    date: "Fecha de Operación",
                    account: "Cuenta",
                    concept: "Concepto",
                    conceptPlaceholder: "Escriba un concepto o seleccione uno existente",
                    balance: "Valor",
                    trm: "TRM",
                    functionalBalance: "Valor Funcional",
                    save: "Guardar Kardex",
                    saving: "Guardando...",
                    cancel: "Cancelar",
                    validity: "Vigencia",
                    waitingData: "Esperando datos...",
                    noData: "No hay datos disponibles",
                    noMatches: "No se encontraron resultados",
                    loading: "Cargando...",
                    selectFilters: "Seleccione un grupo o compañía y consulte",
                    searchPlaceholder: "Buscar en la tabla...",
                    records: "registros",
                    group: "Grupo",
                    createFirstRecord: "Haga clic en 'Crear' para agregar un nuevo registro",
                    initialBalance: "Saldo Inicial",
                    movement: "Movimiento",
                    finalBalance: "Saldo Final",
                    filterCuenta: "Filtrar cuenta...",
                    filterConcepto: "Filtrar concepto...",
                    filterFecha: "Filtrar fecha..."
                }
            } else {
                return {
                    title: "Heritage Kardex",
                    selectGroup: "Select Group",
                    selectCompany: "Select Company",
                    selectAccount: "Select an account",
                    consultar: "Consult",
                    create: "Create",
                    exportExcel: "Export Excel",
                    createKardex: "New Kardex",
                    editKardex: "Edit Kardex",
                    edit: "Edit",
                    delete: "Delete",
                    date: "Operation Date",
                    account: "Account",
                    concept: "Concept",
                    conceptPlaceholder: "Write a concept or select an existing one",
                    balance: "Value",
                    trm: "TRM",
                    functionalBalance: "Functional Value",
                    save: "Save Kardex",
                    saving: "Saving...",
                    cancel: "Cancel",
                    validity: "Validity",
                    waitingData: "Waiting for data...",
                    noData: "No data available",
                    noMatches: "No results found",
                    loading: "Loading...",
                    selectFilters: "Select a group or company and consult",
                    searchPlaceholder: "Search in table...",
                    records: "records",
                    group: "Group",
                    createFirstRecord: "Click 'Create' to add a new record",
                    initialBalance: "Initial Balance",
                    movement: "Movement",
                    finalBalance: "Final Balance",
                    filterCuenta: "Filter account...",
                    filterConcepto: "Filter concept...",
                    filterFecha: "Filter date..."
                }
            }
        },
        confirmLabels() {
            if (this.$store.state._language == "es-ES") {
                return {
                    title: "Confirmar eliminación",
                    infoTitle: "¿Está seguro?",
                    infoText: "¿Está seguro que desea eliminar este registro? Esta acción no se puede deshacer.",
                    confirm: "Eliminar",
                    cancel: "Cancelar"
                }
            } else {
                return {
                    title: "Confirm deletion",
                    infoTitle: "Are you sure?",
                    infoText: "Are you sure you want to delete this record? This action cannot be undone.",
                    confirm: "Delete",
                    cancel: "Cancel"
                }
            }
        },
        modalTitle() {
            return this.modoEdicion ? this.labels.editKardex : this.labels.createKardex
        },
        modalInfoTitle() {
            return this.modoEdicion ? "Editar Kardex" : "Crear Nuevo Kardex"
        },
        modalInfoText() {
            return this.modoEdicion 
                ? "Actualiza la información del kardex y guarda los cambios." 
                : "Ingresa los datos del nuevo kardex y guarda el registro."
        },
        camposTabla() {
            return [
                { key: 'cuenta', label: 'Cuenta', sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'fecha_operacion', label: 'Fecha', sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'concepto', label: 'Concepto', sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'local', label: 'Valor', sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
                { key: 'trm', label: 'TRM', sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
                { key: 'funcional1', label: 'Valor Funcional', sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                // { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }  // <-- COMENTA ESTA LÍNEA
            ]
        },
        tableFilterDefs() {
            return [
                { key: 'cuenta', type: 'text', placeholder: this.labels.filterCuenta },
                { key: 'concepto', type: 'text', placeholder: this.labels.filterConcepto },
                { key: 'fecha_operacion', type: 'text', placeholder: this.labels.filterFecha }
            ]
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: "Filas por página:",
                noSort: "Sin orden",
                sortAsc: "A-Z / Menor a mayor",
                sortDesc: "Z-A / Mayor a menor",
                columns: "Columnas",
                showAllColumns: "Mostrar todas",
                filters: "Filtros",
                clearFilters: "Limpiar filtros"
            }
        },
        itemsMostrar() {
            let items = this.kardexList
            
            if (this.filtroCompania !== null && this.filtroCompania !== null) {
                items = items.filter(item => item.compania_id === this.filtroCompania)
            }
            
            return items
        },
        itemsFiltrados() {
            if (!this.searchTerm) {
                return this.itemsMostrar
            }
            
            const search = this.searchTerm.toLowerCase().trim()
            return this.itemsMostrar.filter(item => {
                return (
                    (item.cuenta && item.cuenta.toLowerCase().includes(search)) ||
                    (item.concepto && item.concepto.toLowerCase().includes(search)) ||
                    (item.fecha_operacion && item.fecha_operacion.includes(search))
                )
            })
        },
        cuentasOptions() {
            return this.cuentasDisponibles.map(c => ({
                value: c,
                text: c
            }))
        },
        showToolbarExtras() {
            return this.fgl_consulta && this.kardexList.length > 0
        },
        // Verificar si la vigencia está bloqueada (periodo cerrado hacia atrás)
        isVigenciaBloqueada() {
            let fecha = this.formData.fecha_operacion
            
            if (!fecha && this.itemsMostrar.length > 0) {
                const sorted = [...this.itemsMostrar].sort((a, b) => 
                    new Date(a.fecha_operacion) - new Date(b.fecha_operacion)
                )
                if (sorted.length > 0) {
                    fecha = sorted[0].fecha_operacion
                }
            }
            
            if (!fecha) return false
            
            const dateObj = new Date(fecha)
            const vigenciaEvaluar = `${dateObj.getFullYear()}${String(dateObj.getMonth() + 1).padStart(2, '0')}`
            
            const vigencias = new Set()
            this.itemsMostrar.forEach(item => {
                if (item.fecha_operacion) {
                    const d = new Date(item.fecha_operacion)
                    const vig = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`
                    vigencias.add(vig)
                }
            })
            
            if (this.formData.fecha_operacion) {
                const d = new Date(this.formData.fecha_operacion)
                const vig = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`
                vigencias.add(vig)
            }
            
            let periodoMasRecienteCerrado = null
            
            for (const vig of vigencias) {
                if (!this.$store.getters.isPeriodoEnabled(vig)) {
                    if (!periodoMasRecienteCerrado || parseInt(vig) > parseInt(periodoMasRecienteCerrado)) {
                        periodoMasRecienteCerrado = vig
                    }
                }
            }
            
            if (!periodoMasRecienteCerrado) return false
            
            return parseInt(vigenciaEvaluar) <= parseInt(periodoMasRecienteCerrado)
        },
        hasUnsavedChanges() {
            if (!this.originalFormData || this.guardando) return false
            
            const current = {
                fecha_operacion: this.formData.fecha_operacion,
                cuenta: this.formData.cuenta,
                concepto: this.formData.concepto,
                local: this.formData.local,
                trm: this.formData.trm,
                funcional1: this.formData.funcional1
            }
            
            const original = {
                fecha_operacion: this.originalFormData.fecha_operacion,
                cuenta: this.originalFormData.cuenta,
                concepto: this.originalFormData.concepto,
                local: this.originalFormData.local,
                trm: this.originalFormData.trm,
                funcional1: this.originalFormData.funcional1
            }
            
            return JSON.stringify(current) !== JSON.stringify(original)
        }
    },
    mounted() {
        this.inicializar()
    },
    methods: {
        goBack() {
            this.$router.back()
        },

        onChange() {
            // Método para detectar cambios en el formulario
        },

        async inicializar() {
            try {
                this.isLoading = true
                
                const [companiesRes, conceptosRes, gruposRes] = await Promise.all([
                    Company.getAllCompanies(),
                    TasasKardexService.getConceptos(),
                    Grupo.getAllGrupos()
                ])
                
                let companies = []
                if (companiesRes.data && companiesRes.data.companies) {
                    companies = companiesRes.data.companies
                } else if (Array.isArray(companiesRes.data)) {
                    companies = companiesRes.data
                } else if (companiesRes.companies) {
                    companies = companiesRes.companies
                } else if (Array.isArray(companiesRes)) {
                    companies = companiesRes
                }
                
                this.companiasList = companies.filter(c => {
                    if (c.estado === undefined || c.estado === null) return true
                    return c.estado === 1 || c.estado === '1'
                })
                
                this.filteredCompany = this.companiasList
                this.conceptosExistentes = conceptosRes.data.conceptos || []
                this.listGrupos = gruposRes.data.grupos || []
                
            } catch (error) {
                console.error('Error al inicializar TasasKardex:', error)
                this.$toast.error('Error al cargar los datos iniciales')
            } finally {
                this.isLoading = false
            }
        },

        async recargarConceptos() {
            try {
                const conceptosRes = await TasasKardexService.getConceptos()
                this.conceptosExistentes = conceptosRes.data.conceptos || []
            } catch (error) {
                console.error('Error al recargar conceptos:', error)
            }
        },

        onGrupoChange(valor = null) {
            console.log('========================================')
            console.log('🟠 CAMBIO DE GRUPO')
            console.log('========================================')

            console.log('valor recibido por @change:', valor)
            console.log('tipo valor:', typeof valor)
            
            console.log('this.filtroGrupo:', this.filtroGrupo)
            console.log('tipo this.filtroGrupo:', typeof this.filtroGrupo)

            console.log('listGrupos:', this.listGrupos)
            console.log('listCompany (companiasList):', this.companiasList)

            // Determinar el ID del grupo seleccionado
            const grupoIdSeleccionado = valor !== null && valor !== undefined ? valor : this.filtroGrupo

            console.log('🎯 grupoIdSeleccionado:', grupoIdSeleccionado)
            console.log('🎯 tipo grupoIdSeleccionado:', typeof grupoIdSeleccionado)

            // Si no hay grupo seleccionado o es vacío, mostrar todas las compañías
            if (!grupoIdSeleccionado || grupoIdSeleccionado === '' || grupoIdSeleccionado === 'null') {
                console.log('🟢 TODOS LOS GRUPOS')
                this.filteredCompany = [...this.companiasList]

                console.log('Compañías resultantes:', this.filteredCompany)

                this.filtroCompania = null
                this.kardexList = []
                this.fgl_consulta = false
                this.searchTerm = ''
                this.currentPage = 1

                return
            }

            // Convertir a string para comparación
            const grupoIdStr = String(grupoIdSeleccionado).trim()

            console.log('🔎 ID FINAL DEL GRUPO:', grupoIdStr)

            // Filtrar compañías que pertenecen al grupo
            this.filteredCompany = this.companiasList.filter(company => {
                console.log('-----------------------------')
                console.log('Compañía:', company?.nombre)
                console.log('id compañía:', company?.id)
                console.log('id_grupos:', company?.id_grupos)
                console.log('tipo id_grupos:', typeof company?.id_grupos)

                // Si la compañía no tiene id_grupos, no pertenece a ningún grupo
                if (!company || company.id_grupos == null || company.id_grupos === '') {
                    console.log('❌ Compañía sin id_grupos')
                    return false
                }

                // Convertir id_grupos a array de strings (maneja "4,2" o "5")
                const gruposCompania = String(company.id_grupos)
                    .split(',')
                    .map(id => String(id).trim())
                    .filter(Boolean)

                console.log('Grupos de la compañía:', gruposCompania)
                console.log('Buscando grupo:', grupoIdStr)

                // Verificar si el grupo seleccionado está en los grupos de la compañía
                const pertenece = gruposCompania.includes(grupoIdStr)

                console.log(
                    pertenece
                        ? '✅ MATCH'
                        : '❌ NO MATCH'
                )

                return pertenece
            })

            // Resetear la compañía seleccionada
            this.filtroCompania = null
            this.kardexList = []
            this.fgl_consulta = false
            this.searchTerm = ''
            this.currentPage = 1

            console.log('========================================')
            console.log('🏁 RESULTADO FINAL')
            console.log('========================================')
            console.log('Grupo seleccionado:', grupoIdStr)
            console.log('Compañías filtradas:', this.filteredCompany)
            console.log('Cantidad:', this.filteredCompany.length)
        },

        onFiltroChange() {
            this.kardexList = []
            this.fgl_consulta = false
            this.searchTerm = ''
            this.currentPage = 1
        },

        async consultarDatos() {
            if (!this.filtroCompania) {
                this.$toast.error('Seleccione una compañía.')
                return
            }
            
            try {
                this.isLoading = true
                const response = await TasasKardexService.getAllTasasKardex()
                this.kardexList = response.data.listKardex || []
                this.fgl_consulta = true
                this.currentPage = 1
                this.searchTerm = ''
                await this.recargarConceptos()
                
            } catch (error) {
                console.error('Error al consultar datos:', error)
                this.$toast.error('Error al consultar los datos')
                this.kardexList = []
                this.fgl_consulta = true
            } finally {
                this.isLoading = false
            }
        },

        abrirModalCrear() {
            if (!this.filtroCompania) {
                this.$toast.warning('Primero debe seleccionar una compañía')
                return
            }
            
            if (this.isVigenciaBloqueada) {
                this.$toast.error('El periodo está cerrado. No se puede crear.')
                return
            }
            
            this.recargarConceptos()
            this.modoEdicion = false
            this.kardexEditId = null
            this.resetFormData()
            this.showModalKardex = true
        },

        editarKardex(id) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('El periodo está cerrado. No se puede editar.')
                return
            }
            
            const kardex = this.kardexList.find(item => item.id === id)
            if (!kardex) {
                this.$toast.error('No se encontró el registro')
                return
            }
            
            this.modoEdicion = true
            this.kardexEditId = id
            
            this.formData = {
                fecha_operacion: kardex.fecha_operacion,
                cuenta: kardex.cuenta,
                concepto: kardex.concepto,
                local: kardex.local,
                trm: kardex.trm,
                funcional1: kardex.funcional1,
                funcional2: kardex.funcional2
            }
            
            this.originalFormData = { ...this.formData }
            
            this.cargarCuentasParaEdicion(kardex.fecha_operacion, kardex.cuenta)
            this.showModalKardex = true
        },

        async cargarCuentasParaEdicion(fecha, cuentaSeleccionada) {
            try {
                const response = await TasasKardexService.getCuentas(
                    this.filtroCompania,
                    fecha
                )
                
                if (response.data.cuentas && response.data.cuentas.length > 0) {
                    this.cuentasDisponibles = response.data.cuentas
                    this.vigenciaEncontrada = response.data.vigencia
                    this.formData.cuenta = cuentaSeleccionada
                } else {
                    this.cuentasDisponibles = []
                    this.vigenciaEncontrada = null
                    this.$toast.warning('No se encontraron cuentas para esta fecha')
                }
            } catch (error) {
                console.error('Error al obtener cuentas:', error)
                this.$toast.error('Error al cargar las cuentas')
            }
        },

        cerrarModal() {
            if (this.hasUnsavedChanges && this.showModalKardex) {
                if (!confirm('¿Tiene cambios sin guardar. ¿Desea salir sin guardar?')) {
                    return
                }
            }
            this.showModalKardex = false
            this.resetFormData()
        },

        resetFormData() {
            if (!this.modoEdicion) {
                this.formData = {
                    fecha_operacion: null,
                    cuenta: null,
                    concepto: '',
                    local: null,
                    trm: null,
                    funcional1: null,
                    funcional2: null
                }
                this.cuentasDisponibles = []
                this.vigenciaEncontrada = null
                this.originalFormData = null
            }
        },

        closeModal() {
            this.showModDel = false
            this.rowDelete = null
        },

        async onFechaChange() {
            if (!this.formData.fecha_operacion) {
                this.cuentasDisponibles = []
                this.vigenciaEncontrada = null
                return
            }
            
            try {
                this.isLoading = true
                const response = await TasasKardexService.getCuentas(
                    this.filtroCompania,
                    this.formData.fecha_operacion
                )
                
                if (response.data.cuentas && response.data.cuentas.length > 0) {
                    this.cuentasDisponibles = response.data.cuentas
                    this.vigenciaEncontrada = response.data.vigencia
                    this.formData.cuenta = null
                } else {
                    this.cuentasDisponibles = []
                    this.vigenciaEncontrada = null
                    this.$toast.warning('No se encontraron cuentas para esta fecha')
                }
            } catch (error) {
                console.error('Error al obtener cuentas:', error)
                this.$toast.error('Error al cargar las cuentas')
            } finally {
                this.isLoading = false
            }
        },

        calcularFuncional() {
            if (this.formData.local && this.formData.trm) {
                const local = parseFloat(this.formData.local)
                const trm = parseFloat(this.formData.trm)
                const resultado = local * trm
                this.formData.funcional1 = Math.round(resultado * 100) / 100
            }
        },

        calcularTRM() {
            if (this.formData.local && this.formData.funcional1 && this.formData.local !== 0) {
                const local = parseFloat(this.formData.local)
                const funcional = parseFloat(this.formData.funcional1)
                const resultado = funcional / local
                this.formData.trm = Math.round(resultado * 10000) / 10000
            }
        },

        formatNumber(valor) {
            if (valor === undefined || valor === null || isNaN(valor)) return '0.00'
            return new Intl.NumberFormat('es-CO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(valor)
        },

        formatCurrency(valor) {
            if (valor === undefined || valor === null || isNaN(valor)) return ''
            return new Intl.NumberFormat('es-CO', {
                style: 'currency',
                currency: 'COP',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(valor)
        },

        async guardarKardex() {
            if (!this.hasUnsavedChanges) {
                this.$toast.warning('No hay cambios para guardar')
                return
            }

            try {
                this.guardando = true
                
                if (this.isVigenciaBloqueada) {
                    this.$toast.error('El periodo está cerrado. No se puede guardar.')
                    return
                }
                
                if (!this.formData.fecha_operacion || !this.formData.cuenta || !this.formData.concepto || 
                    this.formData.local === null || this.formData.trm === null) {
                    this.$toast.warning('Todos los campos son obligatorios')
                    return
                }
                
                const local = parseFloat(this.formData.local)
                const trm = parseFloat(this.formData.trm)
                const funcional1 = parseFloat(this.formData.funcional1 || 0)
                
                const dataToSend = {
                    compania_id: this.filtroCompania,
                    cuenta: this.formData.cuenta,
                    fecha_operacion: this.formData.fecha_operacion,
                    concepto: this.formData.concepto,
                    local: Math.round(local * 100) / 100,
                    trm: Math.round(trm * 10000) / 10000,
                    funcional1: Math.round(funcional1 * 100) / 100,
                    funcional2: parseFloat(this.formData.funcional2 || 0),
                    u_modifica: this.$store.state.userId || 1
                }

                if (this.modoEdicion && this.kardexEditId) {
                    dataToSend.id = this.kardexEditId
                    await TasasKardexService.updateTasasKardex(dataToSend)
                    this.$toast.success('Kardex actualizado exitosamente')
                } else {
                    dataToSend.u_crea = this.$store.state.userId || 1
                    await TasasKardexService.createTasasKardex(dataToSend)
                    this.$toast.success('Kardex creado exitosamente')
                }
                
                this.showModalKardex = false
                this.resetFormData()
                await this.consultarDatos()
                await this.recargarConceptos()
                
            } catch (error) {
                console.error('Error al guardar kardex:', error)
                this.$toast.error('Error al guardar el kardex')
            } finally {
                this.guardando = false
            }
        },

        deleteKardex(id) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('El periodo está cerrado. No se puede eliminar.')
                return
            }
            this.rowDelete = id
            this.showModDel = true
        },

        confirmDel() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('El periodo está cerrado. No se puede eliminar.')
                this.showModDel = false
                return
            }
            
            TasasKardexService.deleteTasasKardex(this.rowDelete)
                .then((res) => {
                    this.$toast.success(res.data.message || 'Registro eliminado exitosamente')
                    this.showModDel = false
                    this.rowDelete = null
                    this.consultarDatos()
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al eliminar el registro')
                })
        },

        // ============================================================
        // EXPORTAR A EXCEL
        // ============================================================
        async exportarExcel() {
            if (this.kardexList.length === 0) {
                this.$toast.warning('No hay datos para exportar')
                return
            }

            try {
                this.isLoading = true

                let datosExportar = this.kardexList.filter(item => 
                    item.compania_id === this.filtroCompania
                )

                if (!Array.isArray(datosExportar) || datosExportar.length === 0) {
                    this.$toast.warning('No hay datos para exportar')
                    return
                }

                const sortedData = [...datosExportar].sort((a, b) => {
                    if (a.cuenta !== b.cuenta) {
                        return a.cuenta.localeCompare(b.cuenta)
                    }
                    return new Date(a.fecha_operacion) - new Date(b.fecha_operacion)
                })

                const cuentasMap = new Map()
                sortedData.forEach(item => {
                    if (!cuentasMap.has(item.cuenta)) {
                        cuentasMap.set(item.cuenta, [])
                    }
                    cuentasMap.get(item.cuenta).push(item)
                })

                const resultados = []

                for (const [, items] of cuentasMap) {
                    if (!Array.isArray(items)) continue
                    let saldoAcumulado = 0
                    
                    items.forEach((item) => {
                        const movimiento = parseFloat(item.local) || 0
                        const saldoInicial = saldoAcumulado
                        saldoAcumulado += movimiento
                        const saldoFinal = saldoAcumulado

                        resultados.push({
                            cuenta: item.cuenta,
                            fecha: item.fecha_operacion,
                            concepto: item.concepto || '',
                            trm: parseFloat(item.trm) || 0,
                            movimiento: movimiento,
                            valorFuncional: parseFloat(item.funcional1) || 0,
                            saldoInicial: saldoInicial,
                            saldoFinal: saldoFinal
                        })
                    })
                }

                resultados.sort((a, b) => {
                    if (a.cuenta !== b.cuenta) return a.cuenta.localeCompare(b.cuenta)
                    return new Date(a.fecha) - new Date(b.fecha)
                })

                const ahora = new Date()
                const anio = ahora.getFullYear()
                const mes = String(ahora.getMonth() + 1).padStart(2, '0')
                const dia = String(ahora.getDate()).padStart(2, '0')
                const fechaStr = `${anio}-${mes}-${dia}`

                const companiaEncontrada = this.companiasList.find(c => c.id === this.filtroCompania)
                const nombreCompania = companiaEncontrada?.nombre || 'Compañía'

                const { Workbook } = XLSX
                const workbook = new Workbook()
                const worksheet = workbook.addWorksheet("Kardex Patrimonio")

                const styles = {
                    tituloPrincipal: {
                        font: { name: "Calibri", size: 16, bold: true },
                        alignment: { horizontal: "center", vertical: "middle" },
                        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    subtitulo: {
                        font: { name: "Calibri", size: 11, bold: true },
                        alignment: { horizontal: "center", vertical: "middle" },
                        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFF" } },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    encabezado: {
                        font: { name: "Calibri", size: 11, bold: true, color: { argb: "000000" } },
                        alignment: { horizontal: "center", vertical: "middle" },
                        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "F2F2F2" } },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    datoNormal: {
                        font: { name: "Calibri", size: 10 },
                        alignment: { horizontal: "left", vertical: "middle" },
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    },
                    numeroNormal: {
                        font: { name: "Calibri", size: 10 },
                        alignment: { horizontal: "right", vertical: "middle" },
                        numFmt: "#,##0.00",
                        border: {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        }
                    }
                }

                const HEADER_ROW = [
                    'Cuenta',
                    'Fecha',
                    'Concepto',
                    'TRM',
                    'Saldo Inicial',
                    'Movimiento',
                    'Saldo Final',
                    'Valor Funcional'
                ]

                const headerColumns = HEADER_ROW.length

                worksheet.mergeCells(`A1:${String.fromCharCode(64 + headerColumns)}1`)
                worksheet.getCell('A1').value = 'KARDEX DE PATRIMONIO'
                Object.assign(worksheet.getCell('A1'), styles.tituloPrincipal)
                worksheet.getCell('A1').font.color = { argb: "000000" }

                worksheet.mergeCells(`A2:${String.fromCharCode(64 + headerColumns)}2`)
                worksheet.getCell('A2').value = `Compañía: ${nombreCompania}`
                Object.assign(worksheet.getCell('A2'), styles.subtitulo)

                worksheet.mergeCells(`A3:${String.fromCharCode(64 + headerColumns)}3`)
                worksheet.getCell('A3').value = `Generado: ${fechaStr} por ${this.$store.state.userName || 'Usuario'}`
                Object.assign(worksheet.getCell('A3'), styles.subtitulo)

                worksheet.addRow([])

                const headerRow = worksheet.addRow(HEADER_ROW)
                headerRow.eachCell((cell) => {
                    Object.assign(cell, styles.encabezado)
                })

                worksheet.columns = [
                    { width: 15 },
                    { width: 15 },
                    { width: 30 },
                    { width: 15 },
                    { width: 18 },
                    { width: 18 },
                    { width: 18 },
                    { width: 18 }
                ]

                resultados.forEach((fila) => {
                    const v_fila = [
                        fila.cuenta,
                        fila.fecha,
                        fila.concepto,
                        fila.trm,
                        fila.saldoInicial,
                        fila.movimiento,
                        fila.saldoFinal,
                        fila.valorFuncional
                    ]

                    const currentRow = worksheet.addRow(v_fila)
                    currentRow.eachCell((cell, colNumber) => {
                        if (colNumber >= 4) {
                            Object.assign(cell, styles.numeroNormal)
                        } else {
                            Object.assign(cell, styles.datoNormal)
                        }
                    })
                })

                worksheet.views = [
                    { state: 'frozen', ySplit: 5 }
                ]

                worksheet.autoFilter = {
                    from: { row: 5, column: 1 },
                    to: { row: 5, column: headerColumns }
                }

                const file = await workbook.xlsx.writeBuffer()
                const downloadLink = document.createElement("a")
                downloadLink.download = `Kardex_Patrimonio_${nombreCompania}_${fechaStr}.xlsx`
                downloadLink.href = window.URL.createObjectURL(
                    new Blob([file], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    })
                )
                downloadLink.style.display = "none"
                document.body.appendChild(downloadLink)
                downloadLink.click()
                downloadLink.remove()

                this.$toast.success('Excel exportado exitosamente')

            } catch (error) {
                console.error('Error al exportar Excel:', error)
                this.$toast.error('Error al exportar el Excel: ' + error.message)
            } finally {
                this.isLoading = false
            }
        }
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
    flex-wrap: wrap;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

.kardex-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-toolbar-export-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f7b4a !important;
    color: white !important;
    border: 1px solid #1f7b4a !important;
}

.table-toolbar-export-btn:hover:not(:disabled) {
    background-color: #16633a !important;
    border-color: #16633a !important;
}

.table-toolbar-export-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.05);
}

.table-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Estilos para los modales */
.uniform-modal-body {
    padding: 0 4px;
}

.password-policy-box {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px 20px;
    border-left: 4px solid #1f4f83;
}

.password-policy-title {
    font-weight: 600;
    font-size: 1.1rem;
    color: #1f4f83;
}

.password-policy-subtitle {
    font-size: 0.9rem;
    color: #6c757d;
    margin-top: 4px;
}

.uniform-form-row {
    margin-bottom: 12px;
}

.uniform-label-col label {
    font-weight: 500;
    margin-bottom: 0;
    color: #495057;
}

.form-input {
    border-radius: 6px;
    border: 1px solid #ced4da;
    padding: 6px 12px;
    width: 100%;
}

.form-input:focus {
    border-color: #1f4f83;
    box-shadow: 0 0 0 0.2rem rgba(31, 79, 131, 0.25);
}

.form-input:disabled {
    background-color: #e9ecef;
}

.reset-dialog-actions {
    border-top: 1px solid #dee2e6;
    padding-top: 16px;
}

.reset-btn-secondary {
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 6px 20px;
    border-radius: 6px;
    cursor: pointer;
}

.reset-btn-secondary:hover {
    background-color: #5a6268;
    color: white;
}

.reset-btn-primary {
    background-color: #1f4f83;
    color: white;
    border: none;
    padding: 6px 20px;
    border-radius: 6px;
    cursor: pointer;
}

.reset-btn-primary:hover:not(:disabled) {
    background-color: #163d67;
    color: white;
}

.reset-btn-primary:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.reset-btn-danger {
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 6px 20px;
    border-radius: 6px;
    cursor: pointer;
}

.reset-btn-danger:hover {
    background-color: #c82333;
    color: white;
}

.uniform-actions-wrap {
    gap: 8px;
}

/* Responsive */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    
    .niif-header .filters-container {
        gap: 4px;
    }
    
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }

    .kardex-toolbar-right {
        flex-wrap: wrap;
        width: 100%;
    }

    .kardex-toolbar-right .table-toolbar-add-btn,
    .kardex-toolbar-right .table-toolbar-export-btn {
        flex: 1 1 auto;
        min-width: 80px;
    }
}
</style>